import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useUpdatePlanAbilityDefaults } from 'app/frontend/base/config/mutations'
import { AbilityType } from 'app/frontend/types/custom/Abilities'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ColorPicker,
  ColorPickerArea,
  ColorPickerContent,
  ColorPickerHueSlider,
  ColorPickerInput,
  ColorPickerSwatch,
  ColorPickerTrigger,
} from '@/components/ui/color-picker'
import { cn } from '@/lib/utils'
import Card from 'app/frontend/base/components/Card'
import { getClass } from 'app/frontend/base/services/characterValues'
import SpellIcon from './SpellIcon'
import {
  compactPlanAbilityDefaults,
  listAbilitiesForDefaultType,
  PlanAbilityDefault,
  PlanAbilityDefaults,
  planAbilityDefaultTypes,
} from './planAbilityDefaults'
import PlanReadonlyAccessAlert from './PlanReadonlyAccessAlert'

const getClassColor = (className?: string) => {
  if (!className) return undefined
  return `var(--color-class-${className.toLowerCase().replace(/\s+/g, '-')})`
}

type ColorDefaultCellProps = {
  value?: string
  disabled: boolean
  onChange: (value: string) => void
  onSave: () => void
}

const ColorDefaultCell = ({
  value,
  disabled,
  onChange,
  onSave,
}: ColorDefaultCellProps) => {
  const openRef = useRef(false)
  return (
    <ColorPicker
      value={value || 'rgb(255, 255, 255)'}
      format='rgb'
      onValueChange={(next) => {
        if (!openRef.current || disabled) return
        if (!value && next === 'rgb(255, 255, 255)') return
        onChange(next)
      }}
      onOpenChange={(open) => {
        openRef.current = open
        if (open || disabled) return
        onSave()
      }}
    >
      <ColorPickerTrigger asChild>
        <Button variant='ghost' className='p-0 h-auto' disabled={disabled}>
          <ColorPickerSwatch className='hover:border-muted-foreground transition-colors w-10 h-7' />
        </Button>
      </ColorPickerTrigger>
      <ColorPickerContent>
        <ColorPickerArea />
        <ColorPickerHueSlider />
        <ColorPickerInput />
      </ColorPickerContent>
    </ColorPicker>
  )
}

const PlanAbilityDefaultsPage = () => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const { accessLevel } = useAuth()
  const canEdit = accessLevel === 'admin'
  const { mutate: updateDefaults, isPending } =
    useUpdatePlanAbilityDefaults(team)
  const [selectedType, setSelectedType] = useState<AbilityType>(() => {
    if (typeof window === 'undefined') return 'key_cooldown'
    const hashType = window.location.hash.replace('#', '')
    if (
      planAbilityDefaultTypes.includes(
        hashType as Exclude<AbilityType, 'assignments'>,
      )
    ) {
      return hashType as AbilityType
    }
    return 'key_cooldown'
  })
  const [draft, setDraft] = useState<PlanAbilityDefaults>(
    () => team.planAbilityDefaults ?? {},
  )
  const draftRef = useRef(draft)
  draftRef.current = draft

  useEffect(() => {
    setDraft(team.planAbilityDefaults ?? {})
  }, [team.planAbilityDefaults])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const nextHash = `#${selectedType}`
    if (window.location.hash === nextHash) return
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}${nextHash}`,
    )
  }, [selectedType])

  const abilities = useMemo(
    () => listAbilitiesForDefaultType(selectedType),
    [selectedType],
  )
  const setField = (
    spellId: number,
    key: keyof PlanAbilityDefault,
    value: string,
  ) => {
    setDraft((current) => ({
      ...current,
      [String(spellId)]: {
        ...current[String(spellId)],
        [key]: value,
      },
    }))
  }
  const save = () => {
    if (!canEdit) return
    const next = compactPlanAbilityDefaults(draftRef.current)
    setDraft(next)
    updateDefaults(next)
  }
  const isDirty =
    JSON.stringify(compactPlanAbilityDefaults(draft)) !==
    JSON.stringify(compactPlanAbilityDefaults(team.planAbilityDefaults ?? {}))
  const isSaving = isDirty || isPending

  return (
    <div className='space-y-4'>
      <div>
        <h1 className='flex items-center gap-3 text-xl font-semibold'>
          <span>{t('dashboard.team.plans.ability-defaults.title')}</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='inline-flex'>
                <Badge variant='outline'>
                  {isSaving ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <Check className='h-3.5 w-3.5 text-success-foreground' />
                  )}
                </Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {isSaving ? 'Saving changes...' : 'Up to date'}
            </TooltipContent>
          </Tooltip>
        </h1>
        <p className='text-sm text-muted-foreground'>
          {t('dashboard.team.plans.ability-defaults.description')}
        </p>
      </div>

      {!canEdit ? <PlanReadonlyAccessAlert /> : null}

      <Tabs
        value={selectedType}
        onValueChange={(value) => setSelectedType(value as AbilityType)}
      >
        <TabsList className='flex-wrap'>
          {planAbilityDefaultTypes.map((type) => (
            <TabsTrigger key={type} value={type} className='h-auto py-1'>
              {t(`dashboard.team.plans.ability-types.${type}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='w-64 h-8 px-3'>
                {t('dashboard.team.plans.ability-defaults.ability')}
              </TableHead>
              <TableHead className='h-8 px-3'>
                {t('dashboard.team.plans.assignments.note-options')}
              </TableHead>
              <TableHead className='h-8 px-3'>
                {t('dashboard.team.plans.assignments.note-tts-options')}
              </TableHead>
              <TableHead className='w-16 h-8 px-3'>
                {t('dashboard.team.plans.ability-defaults.color')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {abilities.map((ability) => {
              const entry = draft[String(ability.id)]
              const className = ability.classId
                ? getClass(ability.classId.toString())
                : undefined
              return (
                <TableRow key={ability.id}>
                  <TableCell className='py-1 px-3'>
                    <a
                      href={`https://wowhead.com/spell=${ability.id}`}
                      target='_blank'
                      rel='noreferrer'
                      className='flex items-center gap-1.5 text-foreground'
                    >
                      <SpellIcon spellId={ability.id} size='medium' />

                      <span
                        className='truncate'
                        style={{ color: getClassColor(className) }}
                      >
                        {ability.name}
                      </span>
                    </a>
                  </TableCell>
                  <TableCell className='py-1 px-3'>
                    <Input
                      value={entry?.message ?? ''}
                      onChange={(event) =>
                        setField(ability.id, 'message', event.target.value)
                      }
                      onBlur={save}
                      disabled={!canEdit}
                      placeholder={ability.name}
                      className={cn(
                        'h-7 py-0',
                        entry?.message &&
                          'text-info-foreground border-info-foreground/50 focus-visible:border-border',
                      )}
                    />
                  </TableCell>
                  <TableCell className='py-1 px-3'>
                    <Input
                      value={entry?.tts_message ?? ''}
                      onChange={(event) =>
                        setField(ability.id, 'tts_message', event.target.value)
                      }
                      onBlur={save}
                      disabled={!canEdit}
                      placeholder={entry?.message || ability.name}
                      className={cn(
                        'h-7 py-0',
                        entry?.tts_message &&
                          'text-info-foreground border-info-foreground/50 focus-visible:border-border',
                      )}
                    />
                  </TableCell>
                  <TableCell className='py-1 px-3'>
                    <ColorDefaultCell
                      value={entry?.rgb}
                      disabled={!canEdit}
                      onChange={(next) => setField(ability.id, 'rgb', next)}
                      onSave={save}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  )
}

export default PlanAbilityDefaultsPage
